/**
 * Datumsrechnung für den Kalender. Alles arbeitet mit Texten der Form JJJJ-MM-TT (Datum) und JJJJ-MM-TTTHH:MM (Uhrzeit in der Zeitzone des Betriebs,
 * Europe/Berlin), nie mit Zeitzonen des Browsers oder Servers. Dadurch rechnen API und Oberfläche gleich, und die Sommerzeit spielt hier keine Rolle:
 * Ein Kalendertag ist immer ein Eintrag in der Liste, egal ob er 23, 24 oder 25 Stunden hat.
 */
export const TIME_ZONE = 'Europe/Berlin';
const DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
/** Gibt es dieses Datum wirklich? „2026-02-31“ gibt es nicht. */
export function isRealDate(date) {
    const m = DATE.exec(date);
    if (!m)
        return false;
    const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
    const probe = new Date(Date.UTC(y, mo - 1, d));
    return probe.getUTCFullYear() === y && probe.getUTCMonth() === mo - 1 && probe.getUTCDate() === d;
}
const toUtc = (date) => new Date(`${date}T12:00:00Z`);
const fromUtc = (d) => d.toISOString().slice(0, 10);
export function addDays(date, n) {
    const d = toUtc(date);
    d.setUTCDate(d.getUTCDate() + n);
    return fromUtc(d);
}
/** Tage von a bis b, b minus a */
export function diffDays(a, b) {
    return Math.round((toUtc(b).getTime() - toUtc(a).getTime()) / 86_400_000);
}
/** Wochentag mit Montag = 0 bis Sonntag = 6 */
export function weekday(date) {
    return (toUtc(date).getUTCDay() + 6) % 7;
}
/** Monat „JJJJ-MM“ eines Datums */
export function monthOf(date) {
    return date.slice(0, 7);
}
export function shiftMonth(month, n) {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(Date.UTC(y, m - 1 + n, 1));
    return d.toISOString().slice(0, 7);
}
export function isMonth(value) {
    return /^\d{4}-(0[1-9]|1[0-2])$/.test(value);
}
/** Heutiges Datum in der Zeitzone des Betriebs */
export function berlinDate(now = new Date()) {
    return new Intl.DateTimeFormat('sv-SE', { timeZone: TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}
/** Datum und Uhrzeit eines Zeitpunkts in der Zeitzone des Betriebs, Stunde 00 bis 23 */
export function berlinWall(at) {
    const parts = new Intl.DateTimeFormat('en-GB', { timeZone: TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(at);
    const p = (type) => parts.find((x) => x.type === type).value;
    return { date: `${p('year')}-${p('month')}-${p('day')}`, time: `${p('hour')}:${p('minute')}` };
}
/**
 * Die Wochen, die ein Monat im Kalender belegt, von Montag der ersten bis Sonntag der letzten Woche. `from` und `to` sind das erste und das
 * letzte Datum des Rasters, `days` alle Tage dazwischen (ein Vielfaches von sieben).
 */
export function monthGrid(month) {
    const first = `${month}-01`;
    const last = addDays(`${shiftMonth(month, 1)}-01`, -1);
    const from = addDays(first, -weekday(first));
    const to = addDays(last, 6 - weekday(last));
    const days = [];
    for (let d = from; d <= to; d = addDays(d, 1))
        days.push(d);
    return { from, to, days };
}
/**
 * Alle Kalendertage, an denen ein Termin stattfindet. Endet ein Termin über mehrere Tage genau um 00:00 Uhr, gehört dieser letzte Tag nicht mehr dazu,
 * denn nach Mitternacht beginnt nichts mehr.
 */
export function coveredDays(a) {
    const first = a.start.slice(0, 10);
    let last = a.end.slice(0, 10);
    if (!a.allDay && a.end.slice(11) === '00:00' && last > first)
        last = addDays(last, -1);
    const days = [];
    for (let d = first; d <= last; d = addDays(d, 1))
        days.push(d);
    return days;
}
/** Beschriftung der Uhrzeit eines Termins an einem bestimmten Tag, zum Beispiel „09:00–10:30“, „ab 14:00“, „ganztägig“ oder „Tag 2 von 3“ */
export function timeLabel(a, day) {
    const days = coveredDays(a);
    const index = days.indexOf(day);
    if (a.allDay)
        return days.length > 1 ? `Ganztägig, Tag ${index + 1} von ${days.length}` : 'Ganztägig';
    const startTime = a.start.slice(11);
    const endTime = a.end.slice(11);
    if (days.length === 1)
        return `${startTime}–${endTime}`;
    if (day === days[0])
        return `ab ${startTime}`;
    if (day === days[days.length - 1] && a.end.slice(0, 10) === day)
        return `bis ${endTime}`;
    return 'ganztägig';
}
/** Montag der Woche, zu der das Datum gehört */
export function weekStart(date) {
    return addDays(date, -weekday(date));
}
/** Kalenderwoche nach ISO 8601 (Woche 1 enthält den ersten Donnerstag des Jahres) mit dem Jahr, zu dem diese Woche zählt */
export function isoWeek(date) {
    const thursday = addDays(date, 3 - weekday(date));
    const year = Number(thursday.slice(0, 4));
    return { year, week: Math.floor(diffDays(`${year}-01-01`, thursday) / 7) + 1 };
}
//# sourceMappingURL=dates.js.map